// The email an office sends new starters before their first day: install
// Dataphyll Crew, sign in, and type the induction team's code. It is copied
// into the office's own mail program (Outlook), so it comes as HTML, which
// keeps its bold and its link when pasted, and as plain text for anything
// that takes only text.

export interface InductionEmailInput {
  // The client the starters are joining: the workspace's name.
  clientName: string
  // The induction team's own code.
  code: string
  // Where a phone is sent to the right app store (/crewapp).
  downloadUrl: string
}

export interface InductionEmail {
  subject: string
  html: string
  text: string
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export function inductionEmail({ clientName, code, downloadUrl }: InductionEmailInput): InductionEmail {
  const client = clientName.trim() || 'us'
  const subject = `Important instructions for working at ${client}`
  const steps: [string, string][] = [
    [
      `<a href="${esc(downloadUrl)}">Download and install</a> the <b>Dataphyll Crew</b> app (iPhone or Android).`,
      `Download and install the Dataphyll Crew app (iPhone or Android): ${downloadUrl}`,
    ],
    ['Open the app and <b>sign in</b> with Apple, Google, Microsoft or Facebook.', 'Open the app and sign in with Apple, Google, Microsoft or Facebook.'],
    ['Follow the prompts and tap <b>"Continue"</b> where asked.', 'Follow the prompts and tap "Continue" where asked.'],
    ['Tap the <b>"Clock on"</b> button.', 'Tap the "Clock on" button.'],
    [`Tap <b>"Enter code manually"</b> and type the code <b>${esc(code)}</b>.`, `Tap "Enter code manually" and type the code ${code}.`],
    [
      `Check the app says <b>"Great, you're inducted into ${esc(client)}"</b>, then tap <b>"Done"</b>. This does not clock you on, so there is nothing to clock off.`,
      `Check the app says "Great, you're inducted into ${client}", then tap "Done". This does not clock you on, so there is nothing to clock off.`,
    ],
  ]
  const intro = `We are excited to welcome you to ${client}! To get started, please follow the steps below to set up your phone before your first day.`
  const why = `At ${client}, we use a platform called Dataphyll to record work. Please complete the steps below before your first day of work.`
  const must = 'Please note: if this is not done in advance, you will not be able to start work.'
  const remind = 'Bring your phone to work every day, fully charged: you will need the app to record your work.'
  const thanks = `Thank you for taking the time to do this. We look forward to seeing you soon at ${client}!`

  const html = [
    `<p>${esc(intro)}</p>`,
    `<p>${esc(why)}<br><b>${esc(must)}</b></p>`,
    '<p><b>Steps to get started:</b></p>',
    `<ol>${steps.map(([h]) => `<li>${h}</li>`).join('')}</ol>`,
    '<p><b>On your first day:</b><br>Your supervisor will show you your team’s Clock On QR. Scan it with the app to start work.</p>',
    `<p><b>Important reminder:</b><br>${esc(remind)}</p>`,
    `<p>${esc(thanks)}</p>`,
    `<p>Best regards,<br>${esc(client)}</p>`,
  ].join('\n')

  const text = [
    intro,
    '',
    why,
    must,
    '',
    'Steps to get started:',
    ...steps.map(([, t], i) => `${i + 1}. ${t}`),
    '',
    'On your first day:',
    'Your supervisor will show you your team’s Clock On QR. Scan it with the app to start work.',
    '',
    'Important reminder:',
    remind,
    '',
    thanks,
    '',
    'Best regards,',
    client,
  ].join('\n')

  return { subject, html, text }
}
